import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CustomerCharter } from '../../portal/charters/charter-payment/charter-payment.component';
import * as moment from 'moment';
import { Moment } from 'moment';
import { CharterDAO } from '../../../core/services/dao/charterDAO';
import { Vessel } from 'anchor-shared/lib/model/entity/vessel';
import { VesselDAO } from '../../../core/services/dao/vesselDAO';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { unsubscribeFromAll } from '../../../core/pure-functions/rx-helper';
import { PaymentDAO } from '../../../core/services/dao/paymentDAO';
import { MooringLocationDAO } from '../../../core/services/dao/mooringLocationDAO';
import { decreaseLatLongPrecision } from '../../../core/pure-functions/geo/lat-long-precision/lat-long-precision-util';
import { CharterAlternateTimeRequestsDAO } from '../../../core/services/dao/charterAlternateTimeRequestsDAO';
import {
  CharterAlternateTimeRequest,
  CustomerResponses,
} from '../../../core/charterAlternateTimeRequest';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Payment } from 'anchor-shared/lib/model/entity/payment';
import { PaymentType } from 'anchor-shared/lib/model/enum/payment-type';

@Component({
  selector: 'app-customer-boat-available-different-times',
  templateUrl: './customer-boat-available-different-time.component.html',
  styleUrls: ['./customer-boat-available-different-time.component.scss'],
})
export class CustomerBoatAvailableDifferentTimeComponent
  implements OnInit, OnDestroy
{
  charter: CustomerCharter;
  charterId: number;
  charterAlternateTimeResponseStatus: string;
  vessel: Vessel;
  subs: Subscription[] = [];
  moreTimes: string[] = [];
  selectedTime: string = null;
  formattedCharterDate: string;
  primaryPayment: Payment;
  mooringLocation;
  waterBodyName: string;
  availableOn: Moment;
  customerResponses = CustomerResponses;
  isMobile: boolean = window && window.screen && window.screen.width < 960;

  constructor(
    private charterDAO: CharterDAO,
    private vesselDAO: VesselDAO,
    private route: ActivatedRoute,
    private router: Router,
    private mooringLocationDAO: MooringLocationDAO,
    private paymentDAO: PaymentDAO,
    private chaterAlternateTimeRequestsDAO: CharterAlternateTimeRequestsDAO,
    private snackBar: MatSnackBar,
    private charterAlternateTimeDAO: CharterAlternateTimeRequestsDAO,
  ) {}

  ngOnInit() {
    this.charterId = this.route.snapshot.params.charterId;
    const charterSub = this.charterDAO
      .getCustomerCharterDetails(this.charterId)
      .subscribe((charter) => {
        this.charter = charter;
        this.availableOn = moment(this.charter.date);
        this.waterBodyName = this.charter.waterBodyDisplayName;
        this.loadCharterVessel();
        this.loadCharterDate();
        this.getPaymentInfo();
      });
    this.subs.push(charterSub);
  }

  loadCharterVessel(): void {
    const vesselSub = this.vesselDAO
      .getByVesselId(this.charter.vesselId)
      .subscribe((vessel) => {
        this.vessel = vessel;
        this.loadCharterResponseStatus();
        this.setMooringLocation();
        this.loadAvailableTimes();
      });
    this.subs.push(vesselSub);
  }

  loadCharterResponseStatus(): void {
    const charterResponseSub = this.charterAlternateTimeDAO
      .getCharterAlternateResponseStatusByCharterId(
        this.charterId,
        this.vessel.id,
      )
      .subscribe((charterResponse) => {
        this.charterAlternateTimeResponseStatus =
          charterResponse.responseStatus;
      });
    this.subs.push(charterResponseSub);
  }

  loadCharterDate(): void {
    this.formattedCharterDate = moment(this.charter.date).format(
      'MMMM Do YYYY',
    );
  }

  getPaymentInfo(): void {
    const paymentSub = this.paymentDAO
      .getPaymentsByCharterId(this.charterId)
      .subscribe((payments) => {
        this.primaryPayment = payments.find((payment) => {
          return payment.type !== PaymentType.REMAINING;
        });
      });
    this.subs.push(paymentSub);
  }

  loadAvailableTimes(): void {
    const alternateTimesRequestSub = this.chaterAlternateTimeRequestsDAO
      .getAllCharterAlternateTimes(this.charterId, this.vessel.id)
      .subscribe((alternateTimeRequest) => {
        this.moreTimes = this.sortTimes(alternateTimeRequest.timeSelections);
      });
    this.subs.push(alternateTimesRequestSub);
  }

  sortTimes(timeArray: string[]): string[] {
    return timeArray.sort((a, b) => {
      return moment(a, 'h:mm A').unix() - moment(b, 'h:mm A').unix();
    });
  }

  setMooringLocation(): void {
    const mooringSub = this.mooringLocationDAO
      .getByVesselId(this.vessel.id)
      .subscribe((location) => {
        this.mooringLocation = location;
      });
    this.subs.push(mooringSub);
  }

  navigateToSearchLocation(): void {
    this.router.navigate(['rental-location-map/' + this.waterBodyName], {
      queryParams: {
        latitude: decreaseLatLongPrecision(this.mooringLocation.lat),
        longitude: decreaseLatLongPrecision(this.mooringLocation.long),
        zoom: this.mooringLocation.zoom ? this.mooringLocation.zoom : '11',
        date: this.availableOn?.format('YYYY-MM-DD'),
      },
    });
  }

  timeSelected(time: string) {
    this.selectedTime = time;
  }

  saveNewStartTimeToCharter(): void {
    if (this.selectedTime) {
      const alternateTimeRequestUpdate: Partial<CharterAlternateTimeRequest> = {
        responseStatus: CustomerResponses.TIME_SELECTED,
        selectedTime: this.selectedTime,
      };
      const updateTimeRequestSub = this.chaterAlternateTimeRequestsDAO
        .updateCharterTimeRequest(
          this.charterId,
          this.vessel.id,
          alternateTimeRequestUpdate,
          this.charter.duration,
        )
        .subscribe((updated) => {
          if (updated) {
            this.getPaymentInfo();
            this.charterAlternateTimeResponseStatus =
              this.customerResponses.TIME_SELECTED;
            this.snackBar.open('Time updated successfully.', null, {
              duration: 4000,
              verticalPosition: 'top',
            });
          }
        });
      this.subs.push(updateTimeRequestSub);
    } else {
      this.snackBar.open('No time selected. Select a time.', null, {
        duration: 4000,
        verticalPosition: 'top',
      });
    }
  }

  noTimesWork(): void {
    const cancelCharterSub = this.charterDAO
      .customerCancelCharterAlternateTimes(this.charterId)
      .subscribe(() => {
        this.updateCharterAlternateTimeStatus();
      });
    this.subs.push(cancelCharterSub);
  }

  updateCharterAlternateTimeStatus() {
    const charterAlternateTimeSub = this.charterAlternateTimeDAO
      .cancelCharterTimeRequest(this.charterId, this.vessel.id)
      .subscribe(() => {
        this.loadCharterResponseStatus();
      });
    this.subs.push(charterAlternateTimeSub);
  }

  navToPayDeposit(): void {
    const paymentSub = this.paymentDAO
      .getPaymentsByCharterId(this.charterId)
      .subscribe((payments) => {
        this.primaryPayment = payments.find((payment) => {
          return payment.type !== PaymentType.REMAINING;
        });
        this.subs.push(paymentSub);
        const sessionTokenSub = this.charterDAO
          .getSessionTokenByCharterId(this.charter.id)
          .subscribe((sessionToken) => {
            if (sessionToken) {
              this.router.navigate([
                `/charters/${this.charter.id}/payments/${this.primaryPayment.id}/${sessionToken.sessionToken}`,
              ]);
            }
          });
        this.subs.push(sessionTokenSub);
      });
  }

  navToCharterDetails(): void {
    this.router.navigate([`customer/charter-details/${this.charter.id}`]);
  }

  getClassForTime(time: string) {
    if (this.selectedTime === time) {
      return 'selected';
    }
    return 'time-option';
  }

  ngOnDestroy() {
    unsubscribeFromAll(this.subs);
  }
}
